import {
  resolveExportAttributesForFeature,
  toAttributesModalData,
  type AttributesClickData,
} from 'components/Attributes/attributeResolvers';
import { isAttributeDetailCategory } from 'components/Attributes/attributeDetailsTypes';
import {
  cancellableMapPool,
  isExportAbortError,
  throwIfExportCancelled,
} from 'utils/shapefileExportCancellation';
import {
  enrichNetworkFeaturesForShapefileExport,
  isNetworkSectionsGeoLayer,
  type MapFeatureLike,
} from 'utils/networkShapefileExport';
import { EXPORT_DETAIL_FETCH_CONCURRENCY } from 'utils/shapefileExportConstants';
import {
  shouldEnrichLayerFeaturesForExport,
  type ShapefileExportLayerContext,
} from 'utils/shapefileExportRules';

const detailCache = new Map<string, Record<string, unknown>>();

function readString(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

function inferResolvableCategory(
  attrs: Record<string, unknown>,
  layerUrl?: string,
): string | undefined {
  const fromFeature = readString(attrs.category);
  if (fromFeature && isAttributeDetailCategory(fromFeature)) {
    return fromFeature;
  }
  if (layerUrl?.includes('/assets/')) {
    return 'asset';
  }
  if (layerUrl?.includes('/accidents/')) {
    return 'accident';
  }
  if (layerUrl?.includes('/surveys/inspections/')) {
    return 'inspection';
  }
  if (layerUrl?.includes('/surveys/')) {
    return 'survey';
  }
  if (layerUrl?.includes('/schemes/programmed/')) {
    return 'programmed scheme';
  }
  if (layerUrl?.includes('/schemes/fwps/')) {
    return 'candidate schemes';
  }
  if (layerUrl?.includes('/schemes/treatments/')) {
    return 'treatment';
  }
  if (layerUrl?.includes('/schemes/')) {
    return 'scheme';
  }
  if (layerUrl?.includes('/performance/')) {
    return 'performance';
  }
  return fromFeature;
}

function toClickData(
  attrs: Record<string, unknown>,
  layerUrl?: string,
): AttributesClickData {
  const modalData = toAttributesModalData(attrs);
  const category = inferResolvableCategory(attrs, layerUrl);
  return {
    ...modalData,
    category: category ?? modalData.category,
  };
}

function entityCacheKey(clickData: AttributesClickData): string | null {
  const category = clickData.category ?? 'unknown';
  if (category === 'asset' && clickData.id != null) {
    return `asset:${clickData.id}`;
  }
  if (category === 'accident' && clickData.name) {
    return `accident:${clickData.name}`;
  }
  if (
    (category === 'survey' ||
      category === 'inspection' ||
      category === 'scheme' ||
      category === 'candidate schemes' ||
      category === 'programmed scheme' ||
      category === 'treatment' ||
      category === 'performance') &&
    (clickData.id != null || clickData.Id != null)
  ) {
    return `${category}:${clickData.id ?? clickData.Id}`;
  }
  if (category === 'network' && clickData.name) {
    return `network:${clickData.name}`;
  }
  if (clickData.name) {
    return `name:${clickData.name}`;
  }
  return null;
}

async function loadExportAttributesForEntities(
  entityKeys: readonly string[],
  clickDataByKey: ReadonlyMap<string, AttributesClickData>,
  onProgress: (message: string) => void,
  signal?: AbortSignal,
): Promise<Map<string, Record<string, unknown>>> {
  const out = new Map<string, Record<string, unknown>>();
  const keysToFetch: string[] = [];

  for (const key of entityKeys) {
    throwIfExportCancelled(signal);
    const cached = detailCache.get(key);
    if (cached) {
      out.set(key, cached);
    } else {
      keysToFetch.push(key);
    }
  }

  if (keysToFetch.length === 0) {
    return out;
  }

  onProgress(`Fetching attributes (0/${keysToFetch.length})…`);

  const pairs = await cancellableMapPool(
    keysToFetch,
    EXPORT_DETAIL_FETCH_CONCURRENCY,
    async (key) => {
      throwIfExportCancelled(signal);
      const clickData = clickDataByKey.get(key);
      if (!clickData) {
        return { key, attrs: null as Record<string, unknown> | null };
      }
      try {
        const attrs = await resolveExportAttributesForFeature(clickData, false);
        return { key, attrs };
      } catch (error) {
        if (isExportAbortError(error, signal)) {
          throw error;
        }
        return { key, attrs: null as Record<string, unknown> | null };
      }
    },
    {
      signal,
      onProgress: (done, total) => {
        onProgress(`Fetching attributes (${done}/${total})…`);
      },
    },
  );

  for (const { key, attrs } of pairs) {
    if (!attrs) continue;
    detailCache.set(key, attrs);
    out.set(key, attrs);
  }

  return out;
}

async function enrichLayerFeaturesForShapefileExport(
  features: MapFeatureLike[],
  layerUrl: string | undefined,
  onProgress: (message: string) => void,
  signal?: AbortSignal,
): Promise<MapFeatureLike[]> {
  if (features.length === 0) return features;

  const clickDataByKey = new Map<string, AttributesClickData>();
  const entityKeys: string[] = [];

  for (const feature of features) {
    throwIfExportCancelled(signal);
    const clickData = toClickData(feature.attributes, layerUrl);
    const key = entityCacheKey(clickData);
    if (!key || clickDataByKey.has(key)) continue;
    clickDataByKey.set(key, clickData);
    entityKeys.push(key);
  }

  if (entityKeys.length === 0) {
    return features;
  }

  const lookup = await loadExportAttributesForEntities(
    entityKeys,
    clickDataByKey,
    onProgress,
    signal,
  );

  throwIfExportCancelled(signal);
  onProgress('Building shapefile records…');

  return features.map((feature) => {
    const clickData = toClickData(feature.attributes, layerUrl);
    const key = entityCacheKey(clickData);
    const fromDetail = key ? lookup.get(key) : undefined;
    return {
      geometry: feature.geometry,
      attributes: {
        ...(fromDetail || {}),
        ...feature.attributes,
      },
    };
  });
}

/*
  Enriches map features with full attributes for shapefile export.
  Uses the same detail APIs as the Attributes modal
 */
export async function enrichFeaturesForShapefileExport(
  features: MapFeatureLike[],
  layerId: string,
  layerUrl: string | undefined,
  onProgress: (message: string) => void,
  signal?: AbortSignal,
  layerContext?: Pick<ShapefileExportLayerContext, 'layerName'>,
): Promise<MapFeatureLike[]> {
  throwIfExportCancelled(signal);
  if (isNetworkSectionsGeoLayer(layerId)) {
    return enrichNetworkFeaturesForShapefileExport(features, onProgress, signal);
  }

  if (
    !shouldEnrichLayerFeaturesForExport({
      layerId,
      layerUrl,
      layerName: layerContext?.layerName,
    })
  ) {
    return features;
  }

  return enrichLayerFeaturesForShapefileExport(
    features,
    layerUrl,
    onProgress,
    signal,
  );
}
